import { Box, Link, Stack, Typography, useSnackbar } from '@chronosphereio/chrono-ui';
import { AlertCircleOutline as WarningIcon } from 'mdi-material-ui';
import LightbulbOutline from 'mdi-material-ui/LightbulbOutline';
import { useCallback, useMemo } from 'react';
import { parseTemplateVariableNames } from '../../../core';
import { useTemplateVariableActions, useTemplateVariableDefinitions } from '../../../dashboards';
import {
  getVariableSuggestions,
  injectVariableIntoPromQLQuery,
  stripPromQLComments,
  type VariableOpportunity,
} from '../lib';

type VariableSuggestionProps = {
  query: string;
  onUpdateQuery?: (query: string, options?: { shouldRun?: boolean; shouldTriggerOnChange?: boolean }) => void;
  variableOpportunities?: VariableOpportunity[];
};

function useMissingVariablesSuggestion(query: string) {
  const snackbar = useSnackbar();
  const definitions = useTemplateVariableDefinitions();
  const { setVariableDefinitions } = useTemplateVariableActions();

  // Find variables in query that are not defined in dashboard
  const missingVariables = useMemo(() => {
    const queryVariables = parseTemplateVariableNames(stripPromQLComments(query));
    const variableNames = definitions.map((d) => d.spec.name);
    return queryVariables.filter((name) => {
      // Ignore global variables
      if (name.startsWith('__')) {
        return false;
      }
      return !variableNames.includes(name);
    });
  }, [definitions, query]);

  // Add variable to dashboard using the suggestion
  const addVariableSuggestion = useCallback(
    (name: string) => {
      const variableSuggestions = getVariableSuggestions(query);
      const variable = variableSuggestions.find((v) => v.spec.name === name);
      if (variable) {
        setVariableDefinitions([...definitions, variable]);
        snackbar.successSnackbar("Added Prometheus variable '" + name + "' to dashboard");
      }
    },
    [query, definitions, setVariableDefinitions, snackbar]
  );

  return {
    missingVariables: missingVariables,
    addVariableSuggestion,
  };
}

function describeOpportunity(opp: VariableOpportunity): string {
  if (opp.source === 'cross-query') {
    const queryWord = opp.crossQueryCount === 1 ? 'query' : 'queries';
    return `seen in ${opp.crossQueryCount} other ${queryWord}`;
  }
  return 'label match';
}

export function PromQLVariableSuggestion(props: VariableSuggestionProps) {
  const { query, onUpdateQuery, variableOpportunities = [] } = props;
  const snackbar = useSnackbar();
  const { missingVariables, addVariableSuggestion } = useMissingVariablesSuggestion(query);

  const applyOpportunity = useCallback(
    (opp: VariableOpportunity) => {
      if (!onUpdateQuery) return;
      const injectedQuery = injectVariableIntoPromQLQuery(query, {
        name: opp.variableName,
        labelName: opp.labelName,
        injectionType: 'LabelMatcher',
      });
      onUpdateQuery(injectedQuery, { shouldTriggerOnChange: true });
      snackbar.successSnackbar(`Injected $${opp.variableName} into label ${opp.labelName}`);
    },
    [query, onUpdateQuery, snackbar]
  );

  if (missingVariables.length === 0 && variableOpportunities.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.5}>
      {missingVariables.length > 0 && (
        <Box data-testid="variable-suggestion">
          <Stack direction="row">
            <WarningIcon fontSize="small" sx={{ mr: 0.5, color: (t) => t.palette.warning.main }} />
            <Typography
              fontSize={12}
              sx={{ color: (t) => (t.featureFlags.panwRebrandStubYear ? undefined : t.palette.grey[500]) }}
            >
              Suggestion: The following variables are not configured in your dashboard. Would you like to configure and
              add them?{' '}
              {missingVariables.map((n) => (
                <Link
                  component="button"
                  type="button"
                  mr={1}
                  key={n}
                  data-testid={`variable-suggestion-add-link-${n}`}
                  aria-label={`Add $${n} variable to dashboard`}
                  onClick={() => {
                    addVariableSuggestion(n);
                  }}
                  sx={{ verticalAlign: 'baseline', fontSize: 'inherit', lineHeight: 'inherit', p: 0, minWidth: 0 }}
                >
                  <code>{`$${n}`}</code>
                </Link>
              ))}
            </Typography>
          </Stack>
        </Box>
      )}

      {variableOpportunities.length > 0 && (
        <Box data-testid="variable-opportunities">
          <Stack direction="row" alignItems="center" spacing={0.375}>
            <LightbulbOutline
              sx={{
                flexShrink: 0,
                fontSize: 16,
                color: (t) => t.palette.info.main,
                lineHeight: 1,
                display: 'block',
              }}
            />
            <Typography fontSize={11} sx={{ color: (t) => t.palette.grey[500], lineHeight: 1.45 }}>
              Suggested variables:{' '}
              {variableOpportunities.map((opp) => (
                <Typography key={`${opp.variableName}:${opp.labelName}`} component="span" fontSize={11}>
                  {onUpdateQuery ? (
                    <Link
                      component="button"
                      type="button"
                      data-testid={`variable-opportunity-inject-${opp.variableName}-${opp.labelName}`}
                      aria-label={`Inject $${opp.variableName} for label ${opp.labelName}`}
                      onClick={() => applyOpportunity(opp)}
                      sx={{ verticalAlign: 'baseline', fontSize: 'inherit', lineHeight: 'inherit', p: 0, minWidth: 0 }}
                    >
                      <code>{`${opp.labelName}=~"$${opp.variableName}"`}</code>
                    </Link>
                  ) : (
                    <code>{`${opp.labelName}=~"$${opp.variableName}"`}</code>
                  )}
                  <Typography
                    component="span"
                    fontSize={10}
                    sx={{ ml: 0.375, mr: 1, color: (t) => t.palette.grey[400] }}
                  >
                    ({describeOpportunity(opp)})
                  </Typography>
                </Typography>
              ))}
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
